/**
 * Central config for all 8 service categories.
 * QE fees are defaults. Admin can override via /api/settings (keys: qe_{slug}_fee_small, etc.)
 */

export const CATEGORIES = [
  {
    slug: 'malning',
    route: '/målning',
    label: 'Målning & tapetsering',
    icon: 'Paintbrush',
    shortLabel: 'Målning',
    seoTitle: 'Målning och tapetsering | Klart Hemma',
    seoDescription: 'Målning och tapetsering som ger rummet ett nytt uttryck. Beskriv ytan och arbetet så får du en prisindikation att utgå från.',
    trustBullets: [
      'Målning av väggar, tak och snickerier',
      'Tapetsering och förarbete efter behov',
      'Prisindikation utifrån rummets storlek och arbetets omfattning',
    ],
    faq: [
      { q: 'Ingår material i priset?', a: 'Nej, priset avser enbart arbetstid. Vi kan hjälpa till att beräkna och beställa material mot tillägg.' },
      { q: 'Hur lång tid tar målning av ett rum?', a: 'Det beror på underlag, förarbete, yta och antal strykningar. Vi stämmer av tidplanen när vi vet vad som ska målas.' },
      { q: 'Vad är ROT-avdrag och hur funkar det?', a: 'För godkänt ROT-arbete kan avdraget vara upp till 30 % av arbetskostnaden. Material ingår inte, och dina villkor avgör hur mycket du kan använda.' },
      { q: 'Kan ni hjälpa till med tapetsering också?', a: 'Ja! Vi utför både tapetsering och avlägsnande av gammal tapet. Välj "Tapetsera" som typ av arbete i formuläret.' },
      { q: 'Hur lång tid i förväg behöver jag boka?', a: 'Kontakta oss med önskad tid och omfattning så stämmer vi av tillgängligheten för just ditt projekt.' },
    ],
    subcategoryCards: null,
    qe: {
      feeSmall: 8900,
      feeStandard: 18900,
      feeLarge: 34900,
      spanDefault: 0.25,
      rotLaborShare: 0.80,
      q1Label: 'Hur stort är utrymmet?',
      q1Options: [
        { key: 'small', label: 'Litet', sub: 'upp till 15 m²' },
        { key: 'standard', label: 'Standard', sub: '15-40 m²' },
        { key: 'large', label: 'Stort', sub: '40+ m²' },
      ],
      q2Label: 'Vad ska göras?',
      q2Options: [
        { key: 'vagg', label: 'Väggar', multiplier: 1.00 },
        { key: 'tak', label: 'Tak', multiplier: 0.85 },
        { key: 'bagg_tak', label: 'Väggar + tak', multiplier: 1.20 },
        { key: 'tapetsera', label: 'Tapetsera', multiplier: 1.15 },
      ],
    },
    wizardParams: {
      min_hours_median: 8,
      paint_wall_h_per_m2: 0.55,
      paint_ceiling_h_per_m2: 0.45,
      paint_trim_h_per_meter: 0.08,
      wallpaper_h_per_m2: 0.22,
      strip_wallpaper_h_per_m2: 0.18,
    },
  },
  {
    slug: 'kok',
    route: '/kök',
    label: 'Köksrenovering',
    icon: 'ChefHat',
    shortLabel: 'Kök',
    seoTitle: 'Köksrenovering | Klart Hemma',
    seoDescription: 'Prisuppskattning på köksrenovering. Rekommenderat pris inkl. moms och ROT-avdrag, anpassat efter ditt kök.',
    trustBullets: [
      'Fast pris baserat på ditt projekts storlek och konfiguration',
      'ROT-avdrag upp till 30 % för privatpersoner',
      'Erfarna montörer med kompetens för ditt projekt',
      'Inkluderar rivning, montering och vitvaror',
      'Snabbuppskattning — få pris direkt (2 min)',
    ],
    faq: [
      { q: 'Ingår köket i priset?', a: 'Nej, priset avser montering. Du beställer köksinredningen separat, eller så hjälper vi till mot tillägg.' },
      { q: 'Hur lång tid tar ett köksbyte?', a: 'Ett standardkök tar vanligtvis 3-5 dagar beroende på storlek och tillval.' },
      { q: 'Kan ni installera el och VVS?', a: 'Ja, vi samordnar el- och VVS-arbeten med certifierade underentreprenörer. Välj det som tillägg i formuläret.' },
      { q: 'Vad kostar ett standardkök att montera?', a: 'Normalt 35 000-45 000 kr inkl. moms för ett standardkök med montering, bänkskiva och vitvaror.' },
      { q: 'Ingår städning efteråt?', a: 'Ja, grovstädning av arbetsplatsen ingår alltid i priset.' },
    ],
    subcategoryCards: null,
    qe: {
      feeSmall: 29900,
      feeStandard: 39900,
      feeLarge: 54900,
      spanDefault: 0.25,
      rotLaborShare: 0.85,
      q1Label: 'Hur stort är köket?',
      q1Options: [
        { key: 'small', label: 'Litet', sub: 'upp till 7 skåp' },
        { key: 'standard', label: 'Standard', sub: '8-11 skåp' },
        { key: 'large', label: 'Stort', sub: '12+ skåp' },
      ],
      q2Label: 'Antal löpande meter bänk',
      q2Options: [
        { key: 'k6', label: '6 m', multiplier: 0.85 },
        { key: 'k8', label: '8 m', multiplier: 1.00 },
        { key: 'k10', label: '10 m', multiplier: 1.10 },
        { key: 'k12', label: '12 m', multiplier: 1.20 },
        { key: 'k14plus', label: '14+ m', multiplier: 1.35 },
      ],
    },
    wizardParams: {
      backsplash_h_per_meter: 4.0,
      backsplash_extra_gips_h: 4.0,
      backsplash_extra_concrete_h: 1.0,
      backsplash_extra_unknown_h: 2.5,
      demolition_fee_small: 6000,
      demolition_fee_standard: 7500,
      demolition_fee_large: 9000,
      disposal_fee_per_run: 5000,
    },
  },
  {
    slug: 'vatrum',
    route: '/våtrum',
    label: 'Våtrumsrenovering',
    icon: 'ShowerHead',
    shortLabel: 'Våtrum',
    seoTitle: 'Våtrumsrenovering | Klart Hemma',
    seoDescription: 'Prisuppskattning på badrumsrenovering och våtrumsarbeten. Se rekommenderat pris inkl. moms och ROT-avdrag direkt.',
    trustBullets: [
      'Fast pris baserat på ditt projekts storlek och konfiguration',
      'ROT-avdrag upp till 30 % för privatpersoner',
      'Erfarna montörer med kompetens för ditt projekt',
      'Inkluderar rivning, montering och vitvaror',
      'Snabbuppskattning — få pris direkt (2 min)',
    ],
    faq: [
      { q: 'Vad kostar en helrenovering av badrum?', a: 'För ett standardbadrum på 5-8 m² kostar en helrenovering normalt 100 000-180 000 kr inkl. moms beroende på material och tillval.' },
      { q: 'Hur lång tid tar en badrumsrenovering?', a: 'En helrenovering tar normalt 2-4 veckor beroende på storlek och komplexitet.' },
      { q: 'Ingår VVS och el i priset?', a: 'VVS och el är tillval som tas med av behöriga underentreprenörer. Det ingår i den totala offerten.' },
      { q: 'Vad är tätskikt och måste det bytas?', a: 'Tätskikt är en vattentät membran under kakel. Det måste alltid bytas vid renovering för att uppfylla BBR-krav.' },
      { q: 'Kan ni renovera utan att riva allt?', a: 'Ja! Vi erbjuder delrenoveringar som ytskiktsrenovering och kakelläggning utan total rivning.' },
    ],
    subcategoryCards: null,
    qe: {
      feeSmall: 69000,
      feeStandard: 119000,
      feeLarge: 179000,
      spanDefault: 0.30,
      rotLaborShare: 0.55,
      q1Label: 'Hur stort är badrummet?',
      q1Options: [
        { key: 'small', label: 'Litet', sub: 'upp till 5 m²' },
        { key: 'standard', label: 'Standard', sub: '5-10 m²' },
        { key: 'large', label: 'Stort', sub: '10+ m²' },
      ],
      q2Label: 'Vad ingår i renoveringen?',
      q2Options: [
        { key: 'ytskikt', label: 'Ytskikt', multiplier: 0.85 },
        { key: 'del', label: 'Delrenovering', multiplier: 1.00 },
        { key: 'hel', label: 'Helrenovering', multiplier: 1.25 },
      ],
    },
    wizardParams: {
      wet_unknown_multiplier: 1.15,
      wet_m2_fee: 8500,
    },
  },
  {
    slug: 'kakel',
    route: '/kakel',
    label: 'Kakel & klinker',
    icon: 'LayoutGrid',
    shortLabel: 'Kakel',
    seoTitle: 'Kakel och klinker | Klart Hemma',
    seoDescription: 'Prisuppskattning på kakelsättning, klinkerläggning och stenarbeten. Se pris inkl. moms och ROT direkt.',
    trustBullets: [
      'Fast pris baserat på ditt projekts storlek och konfiguration',
      'ROT-avdrag upp till 30 % för privatpersoner',
      'Erfarna montörer med kompetens för ditt projekt',
      'Inkluderar rivning, montering och vitvaror',
      'Snabbuppskattning — få pris direkt (2 min)',
    ],
    faq: [
      { q: 'Vad kostar kakelsättning per kvadratmeter?', a: 'Arbetskostnad för kakel är normalt 500-900 kr/m² beroende på kakelstorlekens komplexitet och underlag.' },
      { q: 'Ingår rivning av gammalt kakel?', a: 'Rivning kan läggas till som tillägg. Det kostar normalt 200-350 kr/m².' },
      { q: 'Hur lång tid tar kakelsättning i ett kök?', a: 'Ett stänkskydd på 5 löpmeter tar normalt 1-2 dagar. Ett helt badrum tar 1-2 veckor.' },
      { q: 'Vilken typ av kakel är svårast att lägga?', a: 'Mosaik och stora format (600×600+) kräver mer tid och precision och kostar därmed mer.' },
      { q: 'Ingår fukt/tätskikt?', a: 'I badrum måste tätskikt läggas under kaklet. Det ingår som tillägg i våtrumsjobb.' },
    ],
    subcategoryCards: null,
    qe: {
      feeSmall: 12900,
      feeStandard: 24900,
      feeLarge: 44900,
      spanDefault: 0.25,
      rotLaborShare: 0.65,
      q1Label: 'Hur stor yta?',
      q1Options: [
        { key: 'small', label: 'Liten', sub: 'upp till 10 m²' },
        { key: 'standard', label: 'Standard', sub: '10-25 m²' },
        { key: 'large', label: 'Stor', sub: '25+ m²' },
      ],
      q2Label: 'Var ska kakel läggas?',
      q2Options: [
        { key: 'vagg', label: 'Vägg', multiplier: 1.00 },
        { key: 'golv', label: 'Golv', multiplier: 0.95 },
        { key: 'bagg_golv', label: 'Vägg + golv', multiplier: 1.35 },
      ],
    },
    wizardParams: {
      min_hours_median: 8,
      tile_wall_h_per_m2: 1.25,
      tile_floor_h_per_m2: 1.10,
      tile_demo_h_per_m2: 0.60,
      corner_h_each: 0.35,
      mosaic_multiplier: 1.35,
      large_tile_multiplier: 1.15,
      concrete_tile_multiplier: 1.15,
    },
  },
  {
    slug: 'golv',
    route: '/golv',
    label: 'Golvläggning',
    icon: 'Layers',
    shortLabel: 'Golv',
    seoTitle: 'Golvläggning | Klart Hemma',
    seoDescription: 'Prisuppskattning på parkettgolv, laminat, vinyl och golvslipning. Se pris inkl. moms och ROT-avdrag direkt.',
    trustBullets: [
      'Fast pris baserat på ditt projekts storlek och konfiguration',
      'ROT-avdrag upp till 30 % för privatpersoner',
      'Erfarna montörer med kompetens för ditt projekt',
      'Inkluderar rivning, montering och vitvaror',
      'Snabbuppskattning — få pris direkt (2 min)',
    ],
    faq: [
      { q: 'Vad kostar golvläggning per kvadratmeter?', a: 'Läggning kostar normalt 150-350 kr/m² i arbetstid beroende på golvtyp och förberedelse.' },
      { q: 'Ingår rivning av gammalt golv?', a: 'Rivning och bortforsling av gammalt golv kan läggas till som tillägg.' },
      { q: 'Kan ni slipa parkettgolv?', a: 'Ja! Vi erbjuder slipning och ommålning/oljebehandling av befintliga trägolv.' },
      { q: 'Vad är skillnaden på laminat och vinyl?', a: 'Vinyl är mer vattentåligt och lämpligt i kök och badrum. Laminat är billigare men känsligare för fukt.' },
      { q: 'Ingår listwork i priset?', a: 'Socklar och lister är ett tillägg som läggs till separat per löpmeter.' },
    ],
    subcategoryCards: null,
    qe: {
      feeSmall: 9900,
      feeStandard: 19900,
      feeLarge: 34900,
      spanDefault: 0.25,
      rotLaborShare: 0.70,
      q1Label: 'Hur stor yta?',
      q1Options: [
        { key: 'small', label: 'Liten', sub: 'upp till 20 m²' },
        { key: 'standard', label: 'Standard', sub: '20-50 m²' },
        { key: 'large', label: 'Stor', sub: '50+ m²' },
      ],
      q2Label: 'Typ av arbete',
      q2Options: [
        { key: 'lagga', label: 'Lägga nytt golv', multiplier: 1.00 },
        { key: 'slipa', label: 'Slipa befintligt', multiplier: 1.10 },
        { key: 'matta', label: 'Heltäckningsmatta', multiplier: 0.95 },
      ],
    },
    wizardParams: {
      min_hours_median: 6,
      install_wood_h_per_m2: 0.28,
      install_laminate_h_per_m2: 0.24,
      install_vinyl_h_per_m2: 0.22,
      install_carpet_h_per_m2: 0.25,
      remove_h_per_m2: 0.18,
      sand_h_per_m2: 0.30,
      finish_lacquer_h_per_m2: 0.08,
      finish_oil_h_per_m2: 0.06,
      baseboard_h_per_m: 0.10,
    },
  },
  {
    slug: 'vvs',
    route: '/vvs',
    label: 'VVS',
    icon: 'Wrench',
    shortLabel: 'VVS',
    seoTitle: 'VVS: inkoppling, läckage och flytt | Klart Hemma',
    seoDescription: 'Prisuppskattning på VVS-arbeten: inkoppling, byte av armaturer, stopp, läckage och flytt. Pris direkt inkl. moms.',
    trustBullets: [
      'Fast pris baserat på ditt projekts storlek och konfiguration',
      'ROT-avdrag upp till 30 % för privatpersoner',
      'Erfarna montörer med kompetens för ditt projekt',
      'Inkluderar rivning, montering och vitvaror',
      'Snabbuppskattning — få pris direkt (2 min)',
    ],
    faq: [
      { q: 'Vad kostar byte av blandare?', a: 'Byte av ett par blandare kostar normalt 3 000-5 000 kr inkl. moms.' },
      { q: 'Kan ni fixa stopp i avlopp?', a: 'Ja, vi utför spolning och rensning av avlopp. Normal kostnad 3 000-5 000 kr.' },
      { q: 'Vad kostar det att flytta ett element?', a: 'Flytt av element kostar normalt 8 000-15 000 kr beroende på rörssträcka och åtkomlighet.' },
      { q: 'Ingår material i priset?', a: 'Priset avser arbete. Material faktureras separat till självkostnadspris + pålägg.' },
      { q: 'Hur snabbt kan ni komma vid akut läckage?', a: 'Vi eftersträvar att vara på plats inom 24 timmar vid akuta vattenläckor.' },
    ],
    subcategoryCards: [
      { key: 'inkoppling', label: 'Inkoppling', sub: 'Ny diskho, blandare, dusch', icon: 'Droplet' },
      { key: 'byte', label: 'Byte av armatur', sub: 'Blandare, toalett, dusch', icon: 'RefreshCw' },
      { key: 'stopp', label: 'Stopp / Läckage', sub: 'Rensning, tätning', icon: 'Droplets' },
      { key: 'flytt', label: 'Flytt / Omdragning', sub: 'Rörflytt, element', icon: 'ArrowLeftRight' },
      { key: 'special', label: 'Specialjobb', sub: 'Kontakta oss', icon: 'Phone', isContact: true },
    ],
    qe: {
      feeSmall: 3900,
      feeStandard: 7900,
      feeLarge: 14900,
      spanDefault: 0.25,
      rotLaborShare: 0.60,
      q1Label: 'Hur stort är jobbet?',
      q1Options: [
        { key: 'small', label: 'Litet', sub: 'enkelt byte / stopp' },
        { key: 'standard', label: 'Standard', sub: 'inkoppling / anslutning' },
        { key: 'large', label: 'Stort', sub: 'flytt / renovering' },
      ],
      q2Label: 'Typ av arbete',
      q2Options: [
        { key: 'inkoppling_byte', label: 'Inkoppling / Byte', multiplier: 1.00 },
        { key: 'stopp', label: 'Stopp / Läckage', multiplier: 0.95 },
        { key: 'flytt', label: 'Flytt / Omdragning', multiplier: 1.60 },
      ],
    },
    wizardParams: {
      fee_connect: 3900,
      fee_connect_medium: 6900,
      fee_connect_large: 9900,
      fee_replace_fixture: 3500,
      fee_drain_small: 2900,
      fee_drain_large: 5900,
      fee_move_small: 8900,
      fee_move_medium: 12900,
      fee_move_large: 18900,
      subcontractor_markup_multiplier: 1.30,
    },
  },
  {
    slug: 'el',
    route: '/el',
    label: 'Elinstallation',
    icon: 'Zap',
    shortLabel: 'El',
    seoTitle: 'Elinstallation: uttag, belysning och ny grupp | Klart Hemma',
    seoDescription: 'Prisuppskattning på elarbeten: uttag, belysning, spottar och ny grupp. Pris direkt inkl. moms och ROT.',
    trustBullets: [
      'Fast pris baserat på ditt projekts storlek och konfiguration',
      'ROT-avdrag upp till 30 % för privatpersoner',
      'Erfarna montörer med kompetens för ditt projekt',
      'Inkluderar rivning, montering och vitvaror',
      'Snabbuppskattning — få pris direkt (2 min)',
    ],
    faq: [
      { q: 'Vad kostar ett nytt uttag?', a: 'Montering av ett enkelt uttag kostar normalt 1 500-2 500 kr inkl. moms beroende på åtkomlighet.' },
      { q: 'Kan ni installera downlights/spottar?', a: 'Ja! Montering av belysning och spottar är ett vanligt jobb. Normalt 3 000-6 000 kr för ett rum.' },
      { q: 'Vad innebär en ny grupp?', a: 'En ny säkringsgrupp i elcentralen kan behövas för t.ex. laddbox, bastu eller tung köksutrustning. Kostar normalt 5 000-8 000 kr.' },
      { q: 'Utför ni felsökning?', a: 'Ja, vi utför felsökning och åtgärdar elfel. Normalt debiteras timkostnad + åtgärd.' },
      { q: 'Ingår material i priset?', a: 'Priset avser arbete och standardmaterial. Specialarmaturer eller designuttag tillkommer.' },
    ],
    subcategoryCards: [
      { key: 'inkoppling', label: 'Inkoppling', sub: 'Spis, torktumlare, laddbox', icon: 'Plug' },
      { key: 'uttag', label: 'Uttag / Armatur', sub: 'Nytt uttag eller armatur', icon: 'Lightbulb' },
      { key: 'belysning', label: 'Belysning / Spottar', sub: 'Inbyggd belysning', icon: 'Sun' },
      { key: 'ny_grupp', label: 'Ny grupp / Matning', sub: 'Ny säkringsgrupp', icon: 'Zap' },
      { key: 'felsok', label: 'Felsökning', sub: 'Hitta och åtgärda elfel', icon: 'Search' },
      { key: 'special', label: 'Specialjobb', sub: 'Kontakta oss', icon: 'Phone', isContact: true },
    ],
    qe: {
      feeSmall: 2900,
      feeStandard: 6900,
      feeLarge: 12900,
      spanDefault: 0.25,
      rotLaborShare: 0.60,
      q1Label: 'Hur stort är jobbet?',
      q1Options: [
        { key: 'small', label: 'Litet', sub: 'enstaka uttag / armatur' },
        { key: 'standard', label: 'Standard', sub: 'rum / lösning' },
        { key: 'large', label: 'Stort', sub: 'ny installation / flera rum' },
      ],
      q2Label: 'Typ av arbete',
      q2Options: [
        { key: 'uttag_armatur', label: 'Uttag / Armatur', multiplier: 1.00 },
        { key: 'belysning', label: 'Belysning / Spottar', multiplier: 1.15 },
        { key: 'ny_grupp', label: 'Ny grupp / Matning', multiplier: 1.60 },
        { key: 'felsok', label: 'Felsökning', multiplier: 1.00 },
      ],
    },
    wizardParams: {
      fee_basic: 2900,
      fee_outlet_extra: 1500,
      fee_lighting: 3900,
      fee_new_circuit: 6900,
      fee_troubleshoot: 2900,
      wall_open_multiplier: 1.20,
      long_run_multiplier: 1.15,
      concrete_multiplier: 1.15,
      subcontractor_markup_multiplier: 1.30,
    },
  },
  {
    slug: 'snickeri',
    route: '/snickeri',
    label: 'Snickeri',
    icon: 'Hammer',
    shortLabel: 'Snickeri',
    seoTitle: 'Snickeri: innerväggar, lister och dörrar | Klart Hemma',
    seoDescription: 'Prisuppskattning på snickerijobb: innerväggar, undertak, lister, dörrar och ombyggnader. Pris direkt inkl. moms.',
    trustBullets: [
      'Fast pris baserat på ditt projekts storlek och konfiguration',
      'ROT-avdrag upp till 30 % för privatpersoner',
      'Erfarna montörer med kompetens för ditt projekt',
      'Inkluderar rivning, montering och vitvaror',
      'Snabbuppskattning — få pris direkt (2 min)',
    ],
    faq: [
      { q: 'Vad kostar det att sätta upp en innervägg?', a: 'En enkel regelvägg kostar normalt 8 000-15 000 kr inkl. moms beroende på längd och komplexitet.' },
      { q: 'Kan ni montera nya innerdörrar?', a: 'Ja! Montering av en innerdörr med karm och foder kostar normalt 3 000-5 000 kr.' },
      { q: 'Ingår spackling och målning?', a: 'Spackling och målning är separata arbeten. Vi kan utföra dem mot tillägg, eller ni sköter det själva.' },
      { q: 'Kan ni bygga ett undertak?', a: 'Ja, vi bygger undertak i t.ex. gipsskivor eller mineralull. Kostar normalt 300-600 kr/m².' },
      { q: 'Vad innebär en "mindre ombyggnad"?', a: 'Kombinationsarbeten som att öppna upp mellan rum, flytta dörrar eller skapa en klädkammare.' },
    ],
    subcategoryCards: [
      { key: 'innervagg', label: 'Innervägg', sub: 'Regelvägg med gips', icon: 'Columns2' },
      { key: 'undertak', label: 'Undertak', sub: 'Gips- eller mineralullstak', icon: 'PanelTop' },
      { key: 'lister_socklar', label: 'Lister & socklar', sub: 'Golvsocklar och taklister', icon: 'Ruler' },
      { key: 'dorrar_foder', label: 'Dörrar & foder', sub: 'Innerdörrar och karmar', icon: 'DoorOpen' },
      { key: 'ombyggnad', label: 'Mindre ombyggnad', sub: 'Flytt av dörrar, öppning', icon: 'Hammer' },
      { key: 'special', label: 'Specialjobb', sub: 'Kontakta oss', icon: 'Phone', isContact: true },
    ],
    qe: {
      feeSmall: 7900,
      feeStandard: 17900,
      feeLarge: 34900,
      spanDefault: 0.25,
      rotLaborShare: 0.75,
      q1Label: 'Hur stort är jobbet?',
      q1Options: [
        { key: 'small', label: 'Litet', sub: 'lister, enstaka dörr' },
        { key: 'standard', label: 'Standard', sub: 'vägg eller tak' },
        { key: 'large', label: 'Stort', sub: 'ombyggnad / flera rum' },
      ],
      q2Label: 'Typ av arbete',
      q2Options: [
        { key: 'vagg_undertak', label: 'Vägg / Undertak', multiplier: 1.15 },
        { key: 'lister_dorrar', label: 'Lister / Dörrar', multiplier: 1.00 },
        { key: 'ombyggnad', label: 'Ombyggnad', multiplier: 1.60 },
      ],
    },
    wizardParams: {
      partition_h_per_m: 1.20,
      ceiling_h_per_m2: 0.55,
      trim_h_per_m: 0.10,
      interior_door_h: 2.5,
      exterior_door_h: 5.0,
      casing_h_per_door: 1.5,
      fee_remodel_small: 12900,
      fee_remodel_medium: 24900,
      fee_remodel_large: 39900,
    },
  },
];

/** Lookup by slug */
export function getCategoryBySlug(slug) {
  return CATEGORIES.find((c) => c.slug === slug) || null;
}

/** Map of slug → route for easy reverse lookup */
export const CATEGORY_ROUTE_MAP = Object.fromEntries(
  CATEGORIES.map((c) => [c.slug, c.route])
);

/** Default global settings used by QE when /api/settings is unavailable */
export const QE_DEFAULTS = {
  ROT_RATE: 0.30,
  hourly_private_inc_vat: 875,
  hourly_company_ex_vat: 550,
};
